import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Radio, Volume2, VolumeX, Music, Disc, Wifi, Heart, Plus, X, Send, Check, Play, Pause } from 'lucide-react';

const STREAM_URL = process.env.REACT_APP_STREAM_URL || 'http://localhost:8002';

// Pre-generate donuts outside component so they don't regenerate on re-render
const DONUTS = Array.from({ length: 15 }, (_, i) => ({
  id: i,
  delay: i * 1.5,
  duration: 15 + (i % 5) * 3,
  left: (i * 7) % 100,
  size: 30 + (i % 4) * 15
}));

// Falling donut component
const FallingDonut = ({ delay, duration, left, size }) => (
  <div 
    className="fixed pointer-events-none z-0"
    style={{
      left: `${left}%`,
      top: '-80px',
      animation: `fall ${duration}s linear ${delay}s infinite`,
      fontSize: `${size}px`,
      opacity: 0.8,
      filter: 'saturate(1.3) brightness(1.2)'
    }}
  >
    🍩
  </div>
);

// Off Air Page Component
const OffAirPage = () => (
  <div className="min-h-screen bg-radio-dark flex flex-col items-center justify-center p-4 font-body overflow-hidden relative">
    {/* Falling Donuts Background */}
    {DONUTS.map(donut => (
      <FallingDonut key={donut.id} {...donut} />
    ))}
    
    <div className="fixed inset-0 bg-gradient-to-br from-gray-900 via-radio-dark to-black opacity-90"></div>
    
    <div className="relative z-10 text-center max-w-lg">
      {/* Static TV / Radio */}
      <div className="relative mb-8">
        <div className="w-64 h-64 mx-auto bg-gray-800 rounded-3xl border-8 border-gray-700 shadow-2xl overflow-hidden relative">
          {/* Static noise animation */}
          <div className="absolute inset-0 opacity-30" style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")',
            animation: 'static 0.5s steps(10) infinite'
          }}></div>
          
          {/* Sleeping Homer */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-8xl animate-bounce-subtle">😴</div>
          </div>
          
          {/* Scanlines */}
          <div className="absolute inset-0 pointer-events-none" style={{
            background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.1) 2px, rgba(0,0,0,0.1) 4px)'
          }}></div>
        </div>
        
        {/* Antenna */}
        <div className="absolute -top-8 left-1/2 -translate-x-1/2 flex gap-8">
          <div className="w-1 h-16 bg-gray-600 transform -rotate-12 origin-bottom"></div>
          <div className="w-1 h-16 bg-gray-600 transform rotate-12 origin-bottom"></div>
        </div>
      </div>
      
      {/* Off Air Message */}
      <div className="bg-black/50 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
        <div className="inline-flex items-center gap-2 bg-red-500/20 px-4 py-2 rounded-full border border-red-500/50 mb-4">
          <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
          <span className="text-red-400 font-bold uppercase tracking-wider text-sm">Off Air</span>
        </div>
        
        <h1 className="font-display text-4xl text-white mb-2">D'OH!</h1>
        <h2 className="text-xl text-gray-400 mb-6">We're not on air right now</h2>
        
        <p className="text-gray-500 mb-6">
          Homer's probably napping at the power plant again... 
          <br/>We'll be back with more tunes soon!
        </p>
        
        <div className="flex items-center justify-center gap-4 text-2xl mb-6">
          <span>🍩</span>
          <span>🍺</span>
          <span>📺</span>
          <span>💤</span>
        </div>
        
        <button 
          onClick={() => window.location.reload()} 
          className="bg-gradient-to-r from-radio-orange to-radio-pink text-white font-bold py-3 px-8 rounded-xl hover:scale-105 transition-transform"
        >
          Try Again
        </button>
      </div>
      
      <p className="text-gray-600 text-xs mt-8">🍩 D'oh FM • Springfield's #1 Radio Station 🍩</p>
    </div>
    
    <style>{`
      @keyframes static {
        0% { transform: translateX(0) translateY(0); }
        10% { transform: translateX(-5%) translateY(-5%); }
        20% { transform: translateX(5%) translateY(5%); }
        30% { transform: translateX(-5%) translateY(5%); }
        40% { transform: translateX(5%) translateY(-5%); }
        50% { transform: translateX(-5%) translateY(0); }
        60% { transform: translateX(5%) translateY(0); }
        70% { transform: translateX(0) translateY(5%); }
        80% { transform: translateX(0) translateY(-5%); }
        90% { transform: translateX(5%) translateY(5%); }
        100% { transform: translateX(0) translateY(0); }
      }
    `}</style>
  </div>
);

const App = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTrack, setCurrentTrack] = useState({ title: 'D\'oh FM', artist: 'Click Play to Start' });
  const [analyserData, setAnalyserData] = useState(new Uint8Array(64));
  const [listenerCount, setListenerCount] = useState(0);
  const [isOffline, setIsOffline] = useState(false);
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  
  const [isLoading, setIsLoading] = useState(false);

  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestForm, setRequestForm] = useState({ song_title: '', artist: '', requester_name: '', notes: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [bannerConfig, setBannerConfig] = useState({ enabled: false, link: '', button_text: 'NOW LIVE ON YOUTUBE TOO' });
  
  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const animationRef = useRef(null);
  const canvasRef = useRef(null);
  const audioInitializedRef = useRef(false);
  const isTogglingRef = useRef(false);

  // Draw spectrogram
  const drawSpectrogram = useCallback((dataArray) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    ctx.fillStyle = 'rgba(10, 10, 15, 0.3)';
    ctx.fillRect(0, 0, width, height);
    
    const barCount = 32;
    const barWidth = width / barCount - 2;
    const centerY = height / 2;
    
    for (let i = 0; i < barCount; i++) {
      const value = dataArray[i] || 0;
      const barHeight = (value / 255) * (height * 0.8);
      const x = i * (barWidth + 2) + 1;
      
      const gradient = ctx.createLinearGradient(0, centerY - barHeight/2, 0, centerY + barHeight/2);
      gradient.addColorStop(0, '#ff6b35');
      gradient.addColorStop(0.5, '#f72585');
      gradient.addColorStop(1, '#ff6b35');
      
      ctx.fillStyle = gradient;
      ctx.fillRect(x, centerY - barHeight/2, barWidth, barHeight);
    }
  }, []);

  // Animation loop
  const startVisualization = useCallback(() => {
    const animate = () => {
      if (analyserRef.current) {
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);
        setAnalyserData(dataArray);
        drawSpectrogram(dataArray);
      }
      animationRef.current = requestAnimationFrame(animate);
    };
    animate();
  }, [drawSpectrogram]);

  // Stop visualization
  const stopVisualization = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
  }, []);

  // Play/Pause toggle
  const togglePlay = async () => {
    // Prevent concurrent calls (double-click / click while loading)
    if (isTogglingRef.current) return;
    isTogglingRef.current = true;

    try {
      // If we have audio and it's just paused, simply resume
      if (audioRef.current && audioRef.current.paused && audioRef.current.src && audioInitializedRef.current) {
        try {
          if (audioContextRef.current?.state === 'suspended') {
            await audioContextRef.current.resume();
          }
          audioRef.current.volume = isMuted ? 0 : volume;
          await audioRef.current.play();
          setIsPlaying(true);
          startVisualization();
          return;
        } catch (err) {
          console.log('Resume failed, will reconnect:', err);
        }
      }

      if (isPlaying) {
        // Pause - just pause audio, keep stream alive
        if (audioRef.current) {
          audioRef.current.pause();
        }
        stopVisualization();
        setIsPlaying(false);
      } else {
        // Start fresh connection
        setIsLoading(true);
        try {
          // Create audio context and nodes only once
          if (!audioInitializedRef.current) {
            const audio = new Audio();
            audio.crossOrigin = 'anonymous';
            audio.preload = 'auto';
            audioRef.current = audio;

            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            analyserRef.current = audioContextRef.current.createAnalyser();
            analyserRef.current.fftSize = 128;
            analyserRef.current.smoothingTimeConstant = 0.85;

            sourceRef.current = audioContextRef.current.createMediaElementSource(audio);
            sourceRef.current.connect(analyserRef.current);
            analyserRef.current.connect(audioContextRef.current.destination);

            // When a track finishes naturally, reconnect to get the next one
            audio.onended = () => {
              console.log('Track ended, fetching next...');
              audioRef.current.src = `${STREAM_URL}/stream?t=${Date.now()}`;
              audioRef.current.play().catch(() => {});
            };

            audioInitializedRef.current = true;
          }

          // Resume audio context if suspended
          if (audioContextRef.current.state === 'suspended') {
            await audioContextRef.current.resume();
          }

          // Set volume
          audioRef.current.volume = isMuted ? 0 : volume;

          // Set stream URL
          audioRef.current.src = `${STREAM_URL}/stream?t=${Date.now()}`;

          await audioRef.current.play();
          setIsPlaying(true);
          startVisualization();

          fetch(`${STREAM_URL}/listeners/join`, { method: 'POST' }).catch(() => {});

        } catch (err) {
          console.error('Play error:', err);
          audioInitializedRef.current = false;
          setIsPlaying(false);
        } finally {
          setIsLoading(false);
        }
      }
    } finally {
      isTogglingRef.current = false;
    }
  };

  // Track version for sync
  const trackVersionRef = useRef(0);

  // Reconnect to stream (for when admin changes track)
  const reconnectStream = useCallback(() => {
    if (audioRef.current && audioInitializedRef.current && isPlaying) {
      console.log('Track changed on server, reconnecting...');
      audioRef.current.src = `${STREAM_URL}/stream?t=${Date.now()}`;
      audioRef.current.play().catch(() => {});
    }
  }, [isPlaying]);

  // Fetch track info
  const fetchTrackInfo = useCallback(async () => {
    try {
      const response = await fetch(`${STREAM_URL}/now-playing`);
      if (response.ok) {
        const data = await response.json();
        
        // We're online!
        setIsOffline(false);
        setConnectionAttempts(0);
        
        // Check if track version changed (admin skipped)
        if (data.version !== undefined && trackVersionRef.current !== 0 && data.version !== trackVersionRef.current) {
          reconnectStream();
        }
        trackVersionRef.current = data.version || 0;
        
        setCurrentTrack(data);
      } else {
        throw new Error('Server error');
      }
    } catch (err) {
      // Backend is down
      setConnectionAttempts(prev => prev + 1);
      if (connectionAttempts >= 2) {
        setIsOffline(true);
      }
    }
  }, [reconnectStream, connectionAttempts]);

  // Poll for info
  useEffect(() => {
    fetch(`${STREAM_URL}/config/banner`).then(r => r.json()).then(setBannerConfig).catch(() => {});
  }, []);

  useEffect(() => {
    fetchTrackInfo();
    const interval = setInterval(() => {
      fetchTrackInfo();
      fetch(`${STREAM_URL}/listeners`).then(r => r.json()).then(d => setListenerCount(d.count || 0)).catch(() => {});
    }, 2000); // Poll every 2 seconds for faster sync
    return () => clearInterval(interval);
  }, [fetchTrackInfo]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopVisualization();
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      // Notify server we're leaving
      fetch(`${STREAM_URL}/listeners/leave`, { method: 'POST' }).catch(() => {});
    };
  }, [stopVisualization]);

  // Show offline page if backend is down
  if (isOffline) {
    return <OffAirPage />;
  }

  // Volume
  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : newVolume;
    }
  };

  const toggleMute = () => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    if (audioRef.current) {
      audioRef.current.volume = newMuted ? 0 : volume;
    }
  };

  // Submit request
  const submitRequest = async (e) => {
    e.preventDefault();
    if (!requestForm.song_title.trim()) return;
    
    setIsSubmitting(true);
    try {
      const response = await fetch(`${STREAM_URL}/requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestForm)
      });
      
      if (response.ok) {
        setRequestSuccess(true);
        setRequestForm({ song_title: '', artist: '', requester_name: '', notes: '' });
        setTimeout(() => {
          setRequestSuccess(false);
          setShowRequestModal(false);
        }, 2000);
      }
    } catch (err) {
      console.error('Request error:', err);
    }
    setIsSubmitting(false);
  };

  const avgFreq = analyserData.length ? analyserData.reduce((a, b) => a + b, 0) / analyserData.length : 0;
  const mouthOpen = Math.min(100, avgFreq * 0.5);

  return (
    <div className="min-h-screen bg-radio-dark flex flex-col items-center justify-center p-4 font-body overflow-hidden relative">
      {/* Falling Donuts Background */}
      {DONUTS.map(donut => (
        <FallingDonut key={donut.id} {...donut} />
      ))}
      
      {/* Listener count */}
      <div className="fixed top-4 right-4 z-50 bg-black/50 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/10 flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${isPlaying ? 'bg-green-500 animate-pulse' : 'bg-gray-500'}`}></div>
        <span className="text-gray-400 text-xs">{listenerCount} listening</span>
      </div>
      
      <div className="noise-overlay"></div>
      <div className="fixed inset-0 bg-gradient-to-br from-radio-purple via-radio-dark to-black opacity-80"></div>
      
      {/* Vinyl */}
      <div className="fixed top-10 right-10 opacity-20 hidden md:block pointer-events-none">
        <div className={`w-32 h-32 bg-black rounded-full border-4 border-gray-800 ${isPlaying ? 'animate-vinyl-spin' : ''}`}>
          <div className="absolute inset-1/3 bg-radio-orange rounded-full"></div>
        </div>
      </div>
      
      <div className="relative z-10 w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-black/50 px-6 py-2 rounded-full border border-radio-orange/30 mb-4">
            <Radio className={`w-5 h-5 ${isPlaying ? 'text-radio-orange' : 'text-gray-500'}`} />
            <span className="font-display text-2xl text-radio-orange tracking-wider">D'OH FM</span>
            <div className={`w-2 h-2 rounded-full ${isPlaying ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
          </div>
          <p className="text-gray-400 text-sm">This does not need to exist</p>
          {bannerConfig.enabled && bannerConfig.link && (
            <div className="mt-3">
              <a
                href={bannerConfig.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-red-600/90 hover:bg-red-500 text-white text-sm font-bold py-2 px-4 rounded-lg transition-colors shadow-lg border border-red-500/50"
              >
                <span className="animate-pulse">🔴</span>
                {bannerConfig.button_text || "NOW LIVE ON YOUTUBE TOO"}
              </a>
            </div>
          )}
        </div>
        
        {/* Homer */}
        <div className="relative flex flex-col items-center">
          <div className="relative w-72 h-[420px] flex flex-col items-center">
            {/* Hair */}
            <div className="absolute top-[-30px] left-1/2 -translate-x-1/2 w-36 h-28 z-10">
              <svg viewBox="0 0 100 60" className="w-full h-full stroke-black stroke-[4] fill-none">
                <path d="M30,60 C25,-5 45,-10 45,15" strokeLinecap="round" />
                <path d="M70,60 C75,-5 55,-10 55,15" strokeLinecap="round" />
              </svg>
            </div>

            {/* Head */}
            <div className="relative w-64 h-[380px] bg-homer-yellow border-[6px] border-black rounded-[120px_120px_80px_80px] shadow-2xl flex flex-col items-center overflow-hidden">
              <div className="absolute left-[-4px] top-[55%] w-8 h-12 border-t-4 border-b-4 border-r-4 border-black rounded-r-full transform -translate-y-1/2 opacity-20"></div>

              {/* Eyes */}
              <div className="flex mt-20 z-20 gap-1">
                <div className="w-[100px] h-[100px] bg-white border-[5px] border-black rounded-full flex items-center justify-center shadow-[inset_0_4px_10px_rgba(0,0,0,0.1)]">
                  <div className={`w-3 h-3 bg-black rounded-full ${isPlaying ? 'animate-bounce-subtle' : ''}`}></div>
                </div>
                <div className="w-[100px] h-[100px] bg-white border-[5px] border-black rounded-full flex items-center justify-center shadow-[inset_0_4px_10px_rgba(0,0,0,0.1)]">
                  <div className={`w-3 h-3 bg-black rounded-full ${isPlaying ? 'animate-bounce-subtle' : ''}`}></div>
                </div>
              </div>

              {/* Nose */}
              <div className="absolute top-[42%] left-1/2 -translate-x-1/2 w-14 h-10 bg-homer-yellow border-b-[5px] border-l-[5px] border-r-[5px] border-black rounded-[40%] z-30"></div>

              {/* Muzzle with Mouth */}
              <div className="absolute bottom-[-25px] w-[140%] h-[48%] bg-homer-muzzle border-t-[6px] border-black rounded-[55%_55%_45%_45%] z-10 flex flex-col items-center justify-center">
                <div 
                  className="relative w-[60%] h-24 bg-black/90 rounded-[30px] border-4 border-black overflow-hidden mt-4"
                  style={{ transform: `scaleY(${0.6 + (mouthOpen / 200)})`, transition: 'transform 50ms ease-out' }}
                >
                  <canvas ref={canvasRef} width={200} height={80} className="w-full h-full" />
                  <div className="absolute top-0 left-0 right-0 h-3 bg-white/90 flex justify-center gap-1">
                    {[...Array(6)].map((_, i) => <div key={i} className="w-4 h-full bg-white border-b border-gray-300"></div>)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* PLAY BUTTON - Big and Obvious */}
          <button 
            onClick={togglePlay}
            disabled={isLoading}
            className="relative -mt-6 w-72 h-28 z-20 group cursor-pointer disabled:cursor-wait"
          >
            {/* Collar background */}
            <div className="absolute top-0 left-0 w-1/2 h-full bg-white border-[6px] border-black rounded-tl-[40px] transform skew-x-[22deg] origin-bottom-right transition-all group-hover:bg-gray-100 group-active:translate-y-1"></div>
            <div className="absolute top-0 right-0 w-1/2 h-full bg-white border-[6px] border-black rounded-tr-[40px] transform -skew-x-[22deg] origin-bottom-left transition-all group-hover:bg-gray-100 group-active:translate-y-1"></div>
            
            {/* Big Play/Pause Button */}
            <div className="absolute inset-0 flex items-center justify-center z-30">
              <div className={`
                w-20 h-20 rounded-full flex items-center justify-center
                transition-all transform group-hover:scale-110 group-active:scale-95
                shadow-lg border-4
                ${isLoading
                  ? 'bg-yellow-500 border-yellow-400 shadow-yellow-500/50'
                  : isPlaying 
                    ? 'bg-radio-orange border-orange-400 shadow-orange-500/50' 
                    : 'bg-green-500 border-green-400 shadow-green-500/50 animate-pulse'
                }
              `}>
                {isLoading ? (
                  <div className="w-8 h-8 border-4 border-white/30 border-t-white rounded-full animate-spin" />
                ) : isPlaying ? (
                  <Pause className="w-10 h-10 text-white fill-white" />
                ) : (
                  <Play className="w-10 h-10 text-white fill-white ml-1" />
                )}
              </div>
            </div>
            
            {/* Text below button */}
            {!isPlaying && !isLoading && (
              <div className="absolute -bottom-6 left-0 right-0 text-center">
                <span className="text-green-400 text-sm font-bold animate-pulse">▲ CLICK TO PLAY ▲</span>
              </div>
            )}
            {isLoading && (
              <div className="absolute -bottom-6 left-0 right-0 text-center">
                <span className="text-yellow-400 text-sm font-bold animate-pulse">▲ LOADING... ▲</span>
              </div>
            )}
          </button>
        </div>
        
        {/* Now Playing */}
        <div className="mt-10 bg-black/40 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
          <div className="flex items-center gap-3">
            <Disc className={`w-10 h-10 text-radio-pink flex-shrink-0 ${isPlaying ? 'animate-spin-slow' : ''}`} />
            <div className="flex-1 min-w-0">
              <p className="text-white font-bold truncate">{currentTrack.title}</p>
              <p className="text-gray-400 text-sm truncate">
                {currentTrack.artist && currentTrack.artist !== 'Unknown Artist' ? currentTrack.artist : 'Homer'}
              </p>
            </div>
            <Heart className="w-5 h-5 text-radio-pink cursor-pointer hover:scale-110 transition-transform flex-shrink-0" />
          </div>
        </div>
        
        {/* Volume */}
        <div className="mt-4 flex items-center gap-3 bg-black/30 rounded-xl p-3">
          <button onClick={toggleMute} className="p-1 hover:bg-white/10 rounded-full">
            {isMuted ? <VolumeX className="w-5 h-5 text-gray-400" /> : <Volume2 className="w-5 h-5 text-radio-cyan" />}
          </button>
          <input
            type="range" min="0" max="1" step="0.01" value={volume}
            onChange={handleVolumeChange}
            className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-radio-orange"
          />
          <span className="text-gray-400 text-xs w-8">{Math.round(volume * 100)}%</span>
        </div>
        
        {/* Request Button */}
        <button
          onClick={() => setShowRequestModal(true)}
          className="mt-4 w-full bg-gradient-to-r from-radio-pink to-radio-orange text-white font-bold py-3 px-6 rounded-xl flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform shadow-lg"
        >
          <Plus className="w-5 h-5" />
          Request a Song
        </button>
        
        {/* Status */}
        <div className="mt-4 flex items-center justify-center gap-2 text-sm">
          <Wifi className={`w-4 h-4 ${isPlaying ? 'text-green-500' : 'text-gray-500'}`} />
          <span className={isPlaying ? 'text-green-500' : 'text-gray-500'}>
            {isPlaying ? 'Live from Springfield' : 'Press play to tune in'}
          </span>
        </div>
      </div>
      
      {/* Footer */}
      <div className="relative z-10 mt-8 text-center">
        <p className="text-gray-600 text-xs">🍩 Powered by Duff Beer! 🍺</p>
      </div>
      
      {/* Request Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-radio-purple border border-white/10 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-radio-pink to-radio-orange p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Music className="w-6 h-6 text-white" />
                <h2 className="font-display text-xl text-white">Request a Song</h2>
              </div>
              <button onClick={() => setShowRequestModal(false)} className="p-1 hover:bg-white/20 rounded-full">
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            
            {requestSuccess ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-white font-bold text-lg mb-2">Request Submitted!</h3>
                <p className="text-gray-400 text-sm">D'oh! Thanks for the suggestion!</p>
              </div>
            ) : (
              <form onSubmit={submitRequest} className="p-4 space-y-4">
                <div>
                  <label className="block text-gray-400 text-xs font-bold uppercase mb-1">Song Title *</label>
                  <input
                    type="text" value={requestForm.song_title}
                    onChange={(e) => setRequestForm({...requestForm, song_title: e.target.value})}
                    placeholder="Enter song name..." required
                    className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-radio-orange"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs font-bold uppercase mb-1">Artist</label>
                  <input
                    type="text" value={requestForm.artist}
                    onChange={(e) => setRequestForm({...requestForm, artist: e.target.value})}
                    placeholder="Artist name..."
                    className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-radio-orange"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs font-bold uppercase mb-1">Your Name</label>
                  <input
                    type="text" value={requestForm.requester_name}
                    onChange={(e) => setRequestForm({...requestForm, requester_name: e.target.value})}
                    placeholder="Anonymous"
                    className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-radio-orange"
                  />
                </div>
                <button
                  type="submit" disabled={isSubmitting || !requestForm.song_title.trim()}
                  className="w-full bg-gradient-to-r from-radio-orange to-radio-pink text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-50"
                >
                  {isSubmitting ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Send className="w-5 h-5" />}
                  {isSubmitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
